import { Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, Paper, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiFetch } from '../api/client';
import { useBusiness } from '../contexts/BusinessContext';
import type { UiField, UiSection, UiTab } from '../types/uiSchema';

type FieldDef = {
  id: string;
  label?: string;
  name?: string;
  type?: string;
  required?: boolean;
  allowed_values?: string[];
  placeholder?: string;
  default_value?: unknown;
  accept?: string;
};
type GroupDef = { id?: string; title?: string; name?: string; fields?: FieldDef[] };
type SectionDef = { id: string; title?: string; name?: string; fields?: FieldDef[]; groups?: GroupDef[] };
type FormSchema = { sections?: SectionDef[] };
type UiGroup = { id?: string | null; title?: string; order?: number; fields?: UiField[] };
type RuntimeConfig = {
  id: string;
  name: string;
  status?: string;
  form_schema_json?: FormSchema;
  ui_schema_json?: { field_refs?: string[]; fields?: UiField[]; tabs?: UiTab[] };
};

function humanizeId(id: string): string {
  const base = (id || '').trim().replace(/[_\-.]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!base) return '';
  return base.slice(0, 1).toUpperCase() + base.slice(1);
}

function cleanTitle(title: string | undefined, fallbackId: string): string {
  const raw = (title || '').trim();
  if (!raw) return humanizeId(fallbackId);
  const noBracket = raw.replace(/^\[[A-Za-z0-9_-]{6,}\]\s*/, '').trim();
  const noPrefix = noBracket.replace(/^[A-Za-z0-9]{8,}[_-]\s*/, '').trim();
  if (/^[A-Za-z0-9_-]{8,}$/.test(noPrefix)) return humanizeId(fallbackId);
  return noPrefix || humanizeId(fallbackId);
}

function extractConfigIdFromPath(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  return parts.length >= 2 ? parts[1] : '';
}

function safeEvaluateCondition(condition: string | null | undefined, formData: Record<string, unknown>): boolean {
  if (!condition || !condition.trim()) return true;
  const expr = condition.trim();
  const m = expr.match(/^([a-zA-Z0-9_.-]+)\s*(==|!=)\s*["']?([^"']+)["']?$/);
  if (!m) return false;
  const [, key, op, rhs] = m;
  const lhs = String(formData[key] ?? '');
  return op === '==' ? lhs === rhs : lhs !== rhs;
}

export default function RuntimeFormPage() {
  const { activeTenantId, activeEntityId } = useBusiness();
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [activeSubTabIndex, setActiveSubTabIndex] = useState(0);
  const configId = useMemo(() => extractConfigIdFromPath(window.location.pathname), []);

  /** Canvas split preview loads this page in an iframe; vh-based heights break inside small iframes — use flex + scroll. */
  const isCanvasEmbed = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const q = new URLSearchParams(window.location.search);
    return q.get('embed') === '1' || q.get('canvasPreview') === '1';
  }, []);

  const sections = config?.form_schema_json?.sections || [];
  const globalUiFields = config?.ui_schema_json?.fields || [];
  const uiTabs = (config?.ui_schema_json?.tabs || []).filter(Boolean);

  const load = async () => {
    if (!configId) {
      setError('Invalid runtime URL. Expected /runtime-form/{config_id}.');
      return;
    }
    setError('');
    const resp = await apiFetch(`/api/form-configs/${configId}/runtime`);
    if (!resp.ok) {
      setError(await resp.text());
      return;
    }
    setConfig((await resp.json()) as RuntimeConfig);
  };

  useEffect(() => { void load(); }, [configId]);
  useEffect(() => {
    if (!config?.form_schema_json?.sections) return;
    const defaults: Record<string, unknown> = {};
    for (const sec of config.form_schema_json.sections || []) {
      for (const f of sec.fields || []) {
        if (f.default_value !== undefined) defaults[`${sec.id}.${f.id}`] = f.default_value;
      }
      for (const g of sec.groups || []) {
        for (const f of g.fields || []) {
          if (f.default_value !== undefined) defaults[`${sec.id}.${f.id}`] = f.default_value;
        }
      }
    }
    setFormData((prev) => ({ ...defaults, ...prev }));
  }, [config?.id, config?.form_schema_json]);

  // In iframe embed mode (`embed=1`), the browser may not give `html/body` an explicit height.
  // We use `height: '100%'` for the root layout in that mode, so we must ensure `html/body` are 100% too.
  useEffect(() => {
    if (!isCanvasEmbed) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlHeight = html.style.height;
    const prevBodyHeight = body.style.height;
    const prevBodyMargin = body.style.margin;

    html.style.height = '100%';
    body.style.height = '100%';
    body.style.margin = '0';

    return () => {
      html.style.height = prevHtmlHeight;
      body.style.height = prevBodyHeight;
      body.style.margin = prevBodyMargin;
    };
  }, [isCanvasEmbed]);
  useEffect(() => {
    setActiveTabIndex(0);
    setActiveSubTabIndex(0);
  }, [configId, (config?.id || ''), uiTabs.length]);

  useEffect(() => {
    setActiveSubTabIndex(0);
  }, [activeTabIndex]);

  const submitDraft = async () => {
    if (!activeTenantId || !configId) {
      setError('Select tenant and config context first.');
      return;
    }
    const nextErrors: Record<string, string> = {};
    for (const fld of globalUiFields) {
      const ref = String(fld.field_ref || '').trim();
      if (!ref) continue;
      const schemaField = fieldLookup.get(ref);
      if (fld.visibility === 'conditional' && !safeEvaluateCondition(fld.condition, formData)) continue;
      if (!fld.required) continue;
      const current = formData[ref];
      const isEmptyArray = Array.isArray(current) && current.length === 0;
      const isEmpty = current === null || current === undefined || current === '' || isEmptyArray;
      if (isEmpty) nextErrors[ref] = 'Required';
      const str = String(current ?? '').trim();
      const fieldType = String(schemaField?.type || '').toLowerCase();
      if (!isEmpty && str) {
        if (fieldType === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str)) nextErrors[ref] = 'Invalid email';
        if (fieldType === 'phone' && !/^\+?[0-9()\-\s]{7,20}$/.test(str)) nextErrors[ref] = 'Invalid phone number';
        if ((fieldType === 'link' || fieldType === 'url') && (() => { try { new URL(str); return false; } catch { return true; } })()) {
          nextErrors[ref] = 'Invalid URL';
        }
        if ((fieldType === 'multi_select' || fieldType === 'multiselect' || fieldType === 'checkbox_group') && !Array.isArray(current)) {
          nextErrors[ref] = 'Select one or more values';
        }
      }
    }
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setError('Please fill all required fields before saving.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setInfo('');
    const resp = await apiFetch('/api/form-submissions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tenant_id: activeTenantId,
        entity_id: activeEntityId || undefined,
        config_id: configId,
        status: 'draft',
        data_json: formData,
      }),
    });
    setIsSubmitting(false);
    if (!resp.ok) {
      setError(await resp.text());
      return;
    }
    setInfo('Runtime submission saved.');
  };

  const fieldLookup = useMemo(() => {
    const m = new Map<string, FieldDef>();
    for (const sec of sections) {
      const sid = sec.id;
      for (const f of sec.fields || []) m.set(`${sid}.${f.id}`, f);
      for (const g of sec.groups || []) for (const f of g.fields || []) m.set(`${sid}.${f.id}`, f);
    }
    return m;
  }, [sections]);

  const uiFieldByRef = useMemo(() => {
    const m = new Map<string, UiField>();
    for (const f of globalUiFields) {
      const ref = String(f.field_ref || '').trim();
      if (ref) m.set(ref, f);
    }
    return m;
  }, [globalUiFields]);

  const layoutSxForField = (uiField: UiField, layout: string | undefined): Record<string, unknown> => {
    const spans = uiField.col_spans;
    if (spans && (layout === 'twelve_column' || layout === 'grid' || layout === 'two_column')) {
      const toPct = (v: number | undefined): string | undefined => {
        if (!v) return undefined;
        const n = Math.max(1, Math.min(12, v));
        return `${(n / 12) * 100}%`;
      };
      return {
        flex: '0 0 auto',
        width: {
          xs: toPct(spans.xs) || '100%',
          sm: toPct(spans.sm) || toPct(spans.xs) || '100%',
          md: toPct(spans.md) || toPct(spans.sm) || toPct(spans.xs) || '50%',
          lg: toPct(spans.lg) || toPct(spans.md) || toPct(spans.sm) || toPct(spans.xs) || '50%',
        },
        minWidth: 0,
        boxSizing: 'border-box',
      };
    }
    const col = typeof uiField.col_span === 'number' ? uiField.col_span : undefined;
    if (col && col >= 1 && col <= 12 && (layout === 'twelve_column' || layout === 'grid')) {
      const pct = (col / 12) * 100;
      return {
        flex: `0 0 calc(${pct}% - 8px)`,
        maxWidth: `calc(${pct}% - 8px)`,
        minWidth: 0,
        boxSizing: 'border-box',
      };
    }
    if (layout === 'twelve_column' || layout === 'grid') {
      return uiField.width === 'half'
        ? { flex: '0 0 calc(50% - 8px)', maxWidth: 'calc(50% - 8px)', minWidth: 0, boxSizing: 'border-box' }
        : { flex: '0 0 100%', width: '100%', minWidth: 0 };
    }
    return uiField.width === 'half' ? { width: { xs: '100%', md: '48%' } } : { width: '100%' };
  };

  const alignSx = (uiField: UiField): Record<string, unknown> => {
    const a = uiField.horizontal_align || 'left';
    if (a === 'center') return { justifyContent: 'center', display: 'flex', width: '100%' };
    if (a === 'right') return { justifyContent: 'flex-end', display: 'flex', width: '100%' };
    return {};
  };

  const renderUiField = (uiField: UiField, sectionLayout: string | undefined) => {
    const key = String(uiField.field_ref || '').trim();
    if (!key) return null;
    if (uiField.visibility === 'conditional' && !safeEvaluateCondition(uiField.condition, formData)) return null;
    const schemaField = fieldLookup.get(key);
    if (!schemaField) return null;
    const rawValue = formData[key];
    const value = (rawValue as string | number | undefined) ?? '';
    const widget = (uiField.widget || '').trim().toLowerCase();
    const type = (schemaField.type || 'text').toLowerCase();
    const commonLabel = (schemaField.label || schemaField.name || '').trim() || humanizeId(schemaField.id);
    const helper = fieldErrors[key] || '';
    const isError = Boolean(fieldErrors[key]);
    const widthSx = layoutSxForField(uiField, sectionLayout);
    const wrapAlign = (node: ReactNode) => (
      <Box key={key} sx={{ ...alignSx(uiField), ...widthSx }}>
        {node}
      </Box>
    );

    if (widget === 'checkbox' || type === 'checkbox' || type === 'boolean' || type === 'bool') {
      return wrapAlign(
        <FormControlLabel
          sx={{ m: 0 }}
          control={(
            <Checkbox
              checked={Boolean(rawValue)}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, [key]: e.target.checked }));
                setFieldErrors((prev) => ({ ...prev, [key]: '' }));
              }}
            />
          )}
          label={commonLabel}
        />
      );
    }
    if (widget === 'file' || type === 'file') {
      return wrapAlign(
        <Stack spacing={0.5} sx={{ width: '100%' }}>
          <Button component="label" variant="outlined" size="small">
            Upload file
            <input
              type="file"
              hidden
              accept={schemaField.accept || undefined}
              onChange={(e) => {
                const f = e.target.files?.[0];
                setFormData((prev) => ({ ...prev, [key]: f ? f.name : '' }));
                setFieldErrors((prev) => ({ ...prev, [key]: '' }));
              }}
            />
          </Button>
          {String(rawValue || '').trim() ? (
            <Typography variant="caption" color="text.secondary">Selected: {String(rawValue)}</Typography>
          ) : (
            <Typography variant="caption" color={isError ? 'error.main' : 'text.secondary'}>
              {helper || 'No file selected'}
            </Typography>
          )}
        </Stack>,
      );
    }
    if (type === 'multi_select' || type === 'multiselect' || type === 'checkbox_group') {
      const arrValue = Array.isArray(rawValue) ? rawValue.map(String) : [];
      return wrapAlign(
        <TextField
          size="small"
          sx={{ width: '100%' }}
          select
          SelectProps={{ multiple: true }}
          required={Boolean(uiField.required ?? schemaField.required)}
          error={isError}
          helperText={helper}
          label={commonLabel}
          placeholder={uiField.placeholder || schemaField.placeholder}
          value={arrValue}
          onChange={(e) => {
            const val = e.target.value;
            setFormData((prev) => ({ ...prev, [key]: Array.isArray(val) ? val : String(val).split(',') }));
            setFieldErrors((prev) => ({ ...prev, [key]: '' }));
          }}
        >
          {(schemaField.allowed_values || []).map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </TextField>,
      );
    }
    if (widget === 'dropdown' || widget === 'radio' || type === 'single_select' || type === 'dropdown' || type === 'select') {
      return wrapAlign(
        <TextField
          size="small"
          sx={{ width: '100%' }}
          select
          required={Boolean(uiField.required ?? schemaField.required)}
          error={isError}
          helperText={helper || uiField.help_text}
          label={commonLabel}
          placeholder={uiField.placeholder || schemaField.placeholder}
          value={String(value)}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, [key]: e.target.value }));
            setFieldErrors((prev) => ({ ...prev, [key]: '' }));
          }}
        >
          {(schemaField.allowed_values || []).map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </TextField>,
      );
    }
    const isLongText = widget === 'textarea' || type === 'textarea' || type === 'long_text' || type === 'multiline';
    const htmlType = type === 'number' || type === 'integer'
      ? 'number'
      : (
        type === 'date'
          ? 'date'
          : (
            type === 'email'
              ? 'email'
              : (
                type === 'phone'
                  ? 'tel'
                  : (type === 'link' || type === 'url' ? 'url' : 'text')
              )
          )
      );
    return wrapAlign(
      <TextField
        size="small"
        sx={{ width: '100%' }}
        required={Boolean(uiField.required ?? schemaField.required)}
        type={widget === 'date' ? 'date' : (widget === 'number' ? 'number' : (isLongText ? undefined : htmlType))}
        multiline={isLongText}
        minRows={isLongText ? 3 : undefined}
        placeholder={uiField.placeholder || schemaField.placeholder}
        error={isError}
        helperText={helper || uiField.help_text}
        label={commonLabel}
        value={String(value)}
        onChange={(e) => {
          setFormData((prev) => ({ ...prev, [key]: e.target.value }));
          setFieldErrors((prev) => ({ ...prev, [key]: '' }));
        }}
        InputLabelProps={htmlType === 'date' ? { shrink: true } : undefined}
      />,
    );
  };

  const fallbackTabs: UiTab[] = useMemo(() => {
    return sections.map((s) => {
      const baseFields = (s.fields || []).map((f) => ({
        field_ref: `${s.id}.${f.id}`,
        widget: (f.type || 'text').toLowerCase() === 'date' ? 'date' : ((f.type || 'text').toLowerCase() === 'number' ? 'number' : 'text_input'),
        required: Boolean(f.required),
        width: 'half' as const,
        visibility: 'always' as const,
      }));
      return {
        id: s.id,
        title: cleanTitle((s.title || s.name), s.id),
        sections: [{ id: s.id, title: cleanTitle((s.title || s.name), s.id), layout: 'two_column', fields: baseFields }],
      };
    });
  }, [sections]);

  const effectiveTabs = uiTabs.length > 0 ? uiTabs : fallbackTabs;
  const activeTab = effectiveTabs[Math.max(0, Math.min(activeTabIndex, effectiveTabs.length - 1))];
  const subTabs = activeTab?.sub_tabs || [];
  const activeSubTab = subTabs.length
    ? subTabs[Math.max(0, Math.min(activeSubTabIndex, subTabs.length - 1))]
    : null;
  const tabSections = subTabs.length ? (activeSubTab?.sections || []) : (activeTab?.sections || []);

  const resolveSectionFields = (uiSec: UiSection): UiField[] => {
    const byObject = Array.isArray(uiSec.fields) ? uiSec.fields : [];
    if (byObject.length > 0) return byObject;
    const refs = Array.isArray(uiSec.field_refs) ? uiSec.field_refs : [];
    const out: UiField[] = [];
    for (const r of refs) {
      const ref = String(r || '').trim();
      if (!ref) continue;
      out.push(uiFieldByRef.get(ref) || { field_ref: ref, visibility: 'always', width: 'half' });
    }
    return out;
  };

  const handleCancel = () => {
    setFormData({});
    setFieldErrors({});
    setInfo('Form changes cleared.');
  };

  const scrollableFieldsSx = isCanvasEmbed
    ? {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto' as const,
        pr: 0.5,
        border: '1px solid #e6ebf1',
        borderRadius: 1.5,
        p: 1.25,
        bgcolor: '#fafbfc',
      }
    : {
        maxHeight: { xs: '56vh', md: '62vh' },
        overflowY: 'auto' as const,
        pr: 0.5,
        border: '1px solid #e6ebf1',
        borderRadius: 1.5,
        p: 1.25,
        bgcolor: '#fafbfc',
      };

  return (
    <Box
      sx={
        isCanvasEmbed
          ? {
              height: '100%',
              minHeight: 0,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              p: 0.75,
            }
          : { p: 2 }
      }
    >
      <Paper
        sx={
          isCanvasEmbed
            ? {
                p: 1.25,
                borderRadius: 2,
                border: '1px solid #e6ebf1',
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }
            : { p: 2.5, borderRadius: 2, border: '1px solid #e6ebf1' }
        }
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
          sx={{ mb: 1.5, flexShrink: 0 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#012676' }}>
              {isCanvasEmbed ? 'Runtime preview' : 'Runtime Form (Phase 2 Start)'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {config ? `${config.name} (${config.status || 'unknown'})` : `Config: ${configId}`}
            </Typography>
          </Box>
          {!isCanvasEmbed && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                window.history.pushState({}, '', '/admin/configs');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
            >
              Back To Admin Configs
            </Button>
          )}
        </Stack>
        {error && <Alert severity="error" sx={{ mb: 1.5, flexShrink: 0 }}>{error}</Alert>}
        {info && <Alert severity="info" sx={{ mb: 1.5, flexShrink: 0 }} onClose={() => setInfo('')}>{info}</Alert>}
        <Tabs
          value={activeTabIndex}
          onChange={(_, v) => setActiveTabIndex(Number(v))}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: isCanvasEmbed ? 1 : 1.5, flexShrink: 0, minHeight: isCanvasEmbed ? 36 : undefined }}
        >
          {effectiveTabs.map((t) => (
            <Tab key={t.id} label={cleanTitle(t.title, t.id)} />
          ))}
        </Tabs>

        {subTabs.length > 0 && (
          <Tabs
            value={activeSubTabIndex}
            onChange={(_, v) => setActiveSubTabIndex(Number(v))}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: isCanvasEmbed ? 1 : 1.5, flexShrink: 0, minHeight: isCanvasEmbed ? 36 : undefined }}
          >
            {subTabs.map((t) => (
              <Tab key={t.id} label={cleanTitle(t.title, t.id)} />
            ))}
          </Tabs>
        )}

        <Box sx={scrollableFieldsSx}>
          <Stack spacing={2}>
            {tabSections.map((uiSec) => {
              const layout = uiSec.layout || 'single_column';
              const sectionDensity = uiSec.density || 'comfortable';
              const sectionFields = resolveSectionFields(uiSec);
              return (
                <Paper key={uiSec.id} variant="outlined" sx={{ p: sectionDensity === 'compact' ? 1 : 1.5 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    {cleanTitle(uiSec.title, uiSec.id)}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: sectionDensity === 'compact' ? 0.75 : 1,
                      ...(layout === 'single_column' ? { '& > *': { width: '100%' } } : {}),
                    }}
                  >
                    {sectionFields.map((f) => renderUiField(f, layout))}
                  </Box>

                  {(uiSec.groups || []).length > 0 && (
                    <Stack spacing={1} sx={{ mt: 1.25 }}>
                      {(uiSec.groups || []).map((grp, idx) => (
                        <Paper key={`${uiSec.id}-grp-${grp.id || idx}`} variant="outlined" sx={{ p: 1 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
                            {grp.title || grp.id || `Group ${idx + 1}`}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {(grp.fields || []).map((f) => renderUiField(f, layout))}
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            mt: isCanvasEmbed ? 1 : 2,
            pt: isCanvasEmbed ? 0.5 : 0,
            pb: isCanvasEmbed ? 0.5 : 0,
            px: isCanvasEmbed ? 0.5 : 0,
            bgcolor: isCanvasEmbed ? 'background.paper' : 'transparent',
            borderTop: isCanvasEmbed ? '1px solid #e6ebf1' : 'none',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
          >
            <Button variant="contained" size={isCanvasEmbed ? 'small' : 'medium'} onClick={submitDraft} disabled={isSubmitting || !config}>
              {isSubmitting ? 'Saving...' : (isCanvasEmbed ? 'Save' : 'Save Runtime Submission')}
            </Button>
            <Button variant="outlined" size={isCanvasEmbed ? 'small' : 'medium'} onClick={() => window.print()}>
              Print
            </Button>
            <Button variant="outlined" size={isCanvasEmbed ? 'small' : 'medium'} onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="outlined"
              size={isCanvasEmbed ? 'small' : 'medium'}
              onClick={() => setActiveTabIndex((v) => Math.max(0, v - 1))}
              disabled={activeTabIndex <= 0}
            >
              {isCanvasEmbed ? 'Prev' : 'Previous Tab'}
            </Button>
            <Button
              variant="outlined"
              size={isCanvasEmbed ? 'small' : 'medium'}
              onClick={() => setActiveTabIndex((v) => Math.min(effectiveTabs.length - 1, v + 1))}
              disabled={activeTabIndex >= effectiveTabs.length - 1}
            >
              {isCanvasEmbed ? 'Next' : 'Next Tab'}
            </Button>
            <Button variant="outlined" size={isCanvasEmbed ? 'small' : 'medium'} onClick={() => void load()}>
              {isCanvasEmbed ? 'Reload' : 'Reload Config'}
            </Button>
          </Stack>
        </Box>
      </Paper>

    </Box>
  );
}
